// Every `VITE_*` build variable this app reads, in one schema.
//
// Pure — no `import.meta.env`, no runtime globals — so it can be imported by
// `infra/scripts/env-example.ts` (renders `.env.example`),
// `infra/scripts/env-check.ts` (checks a build's variables), and this app's
// own `env.ts`, without any of them pulling in Vite's client types. See
// `@bindery/shared/env`'s header for why a registry replaces prose tables.
//
// `VITE_FACSIMILE_BASE` exists because of where the facsimiles live. The
// Husserl page scans are ~31 MB of PNGs and the Sein und Zeit facsimile is a
// PDF; none of it is in this repository and none of it ever will be. The
// catalog the ingest emits therefore stores RELATIVE keys
// (`husserl/page012.png`), and this variable is the prefix they hang off — a
// local directory in development, an R2 public bucket in production. One
// catalog, two deliveries, and the switch is a build variable rather than a
// second catalog.
//
// `VITE_CONVEX_URL` is the app's FIRST REQUIRED variable, and it changes what
// a bare clone can do. Before it, `env.ts`'s parse could not fail: one field,
// with a default. Now an unset value throws at module-eval time and the SPA
// does not boot. That is deliberate and is the trade docs/convex.md argues
// for at length — a fallback URL turns "not configured" into "silently
// talking to the wrong deployment" — and it is also what the source repo did,
// where `new ConvexReactClient(import.meta.env.VITE_CONVEX_URL)` threw on
// `undefined` from the same position.
//
// It does NOT change what `bun run build` can do. This app is APP_TYPE "spa"
// and its build is `vite build` alone: the read is compiled into the bundle,
// never evaluated during the build. `apps/retells` fails its build without
// the variable only because its build has a second, node-side prerender pass.
// So a clone with no `.env.local` still builds a bundle here; that bundle
// throws in the browser, which is the loud failure the file that names the
// variable is supposed to produce.
//
// Namespace import, not `import { z }` — zod ships CJS for the node condition
// and the named form resolves to `undefined` under vitest's multi-project run
// (see packages/shared/src/reader/cdn.ts).
import * as z from "zod";
import { envField } from "@bindery/shared/env";

/**
 * The default delivery base, used when the variable is unset.
 *
 * `/facsimile/` is the path `INGEST_COPY_FACSIMILE=1` stages scans to, so an
 * unconfigured `vite dev` serves whatever the developer has locally and a
 * developer with no scans gets 404s on the pane rather than a broken build.
 * Production must override it — 31 MB of page images is not something an
 * asset bundle should carry — and `docs`/README say so.
 */
export const DEFAULT_FACSIMILE_BASE = "/facsimile/";

export const envSchema = z.object({
  /**
   * Public base the facsimile catalog's relative asset keys resolve against.
   * Trailing slash optional — `src/lib/facsimile.ts` normalizes it — so both
   * `https://scans.example/lekterion` and `…/lekterion/` behave the same.
   */
  VITE_FACSIMILE_BASE: envField(z.string().default(DEFAULT_FACSIMILE_BASE), {
    kind: "build",
    doc: "Public base URL the facsimile catalog's relative keys hang off (`husserl/page012.png` → `<base>husserl/page012.png`). The page scans are ~31 MB and live in R2, never in git; point this at the bucket's public base in production. Unset, it defaults to /facsimile/, which is where INGEST_COPY_FACSIMILE=1 stages a local copy for `vite dev`. A trailing slash is optional.",
    placeholder: DEFAULT_FACSIMILE_BASE,
  }),
  /**
   * The Convex deployment this build talks to — the gloss/translation cache,
   * the TTS store, corrections, and the `auth.exchangeCode` hop the sign-in
   * callback needs because the broker's `/auth/exchange` sets no CORS
   * headers.
   *
   * Required, and the only required variable this app has. See this file's
   * header for what that changes and what it does not.
   */
  VITE_CONVEX_URL: envField(z.url(), {
    kind: "build",
    doc: "The Convex deployment URL this build talks to (@bindery/convex-lekterion). Required: src/convexClient.ts constructs the one client from it, and src/env.ts refuses to boot the app without it rather than let a fallback point the reader at the wrong deployment. `bunx convex dev` in packages/convex-lekterion prints it — but read that package's README first, because it attaches to lekterion's existing live deployment rather than provisioning one.",
    placeholder: "https://placeholder-not-a-deployment.convex.cloud",
  }),
});
